// src/components/common/RichTextRenderer.jsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";

const renderTextNode = (node, index, textColorClass, linkColorClass) => {
  let children = <span className={textColorClass}>{node.text}</span>;
  if (node.type === "link") {
    const isInternal = /^\/(?!\/)/.test(node.url);
    const linkClass = linkColorClass || `${textColorClass} underline`;
    if (isInternal) {
      return (
        <RouterLink
          key={`link-${index}`}
          to={node.url}
          className={linkClass}
        >
          {node.children.map((child, childIndex) =>
            renderTextNode(child, childIndex, textColorClass, linkColorClass)
          )}
        </RouterLink>
      );
    }
    return (
      <a
        key={`link-${index}`}
        href={node.url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {node.children.map((child, childIndex) =>
          renderTextNode(child, childIndex, textColorClass, linkColorClass)
        )}
      </a>
    );
  }
  if (node.bold) {
    children = <strong>{children}</strong>;
  }
  if (node.italic) {
    children = <em>{children}</em>;
  }
  if (node.underline) {
    children = <u>{children}</u>;
  }
  if (node.strikethrough) {
    children = <s>{children}</s>;
  }
  if (node.code) {
    children = <code>{children}</code>;
  }
  return <React.Fragment key={`text-${index}`}>{children}</React.Fragment>;
};

const DEFAULT_LIST_FORMAT = "unordered";

/**
 * Strapi often emits nested lists as a *sibling* block right after list-item:
 * [ list-item, list, list-item, list, ... ]
 * Wrapping that inner list in its own <li> creates an empty bullet before the sub-list.
 * Merge those into the previous list-item: <li>text<ul>...</ul></li>
 */
function mergeSiblingNestedListRows(rows) {
  if (!rows || rows.length === 0) return [];
  const out = [];
  let i = 0;
  while (i < rows.length) {
    const cur = rows[i];
    const next = rows[i + 1];

    if (cur?.type === "list-item" && next?.type === "list") {
      out.push({
        kind: "item-with-sibling-nested-list",
        listItem: cur,
        nestedList: next,
      });
      i += 2;
      continue;
    }

    if (cur?.type === "list") {
      out.push({ kind: "orphan-nested-list", nestedList: cur });
      i += 1;
      continue;
    }

    if (cur?.type === "list-item") {
      out.push({ kind: "item", listItem: cur });
      i += 1;
      continue;
    }

    out.push({ kind: "item", listItem: cur });
    i += 1;
  }
  return out;
}

/**
 * <ul>/<ol> only contains <li>. Nested lists:
 * - Inside list-item.children (handled by RichTextListItemBody)
 * - Sibling list after list-item (merged so one <li> contains text + sub-list)
 */
function RichTextList({
  block,
  depth = 0,
  textColorClass,
  linkColorClass,
  renderBlock,
  bodyTextClass,
}) {
  if (!block || block.type !== "list") return null;
  const ListTag = block.format === "ordered" ? "ol" : "ul";
  const rows = block.children || [];
  const merged = mergeSiblingNestedListRows(rows);

  const nestedListClass =
    depth > 0 ? "mt-2 pl-5 sm:pl-6 ml-0 list-outside" : "list-outside";

  return (
    <ListTag
      className={`${textColorClass} ${bodyTextClass} ${
        block.format === "ordered" ? "list-decimal" : "list-disc"
      } ${nestedListClass}`}
    >
      {merged.map((entry, i) => {
        const rowKey = `row-${depth}-${i}`;

        if (entry.kind === "item-with-sibling-nested-list") {
          return (
            <RichTextListItem
              key={rowKey}
              node={entry.listItem}
              siblingNestedList={entry.nestedList}
              depth={depth}
              textColorClass={textColorClass}
              linkColorClass={linkColorClass}
              renderBlock={renderBlock}
              bodyTextClass={bodyTextClass}
            />
          );
        }

        if (entry.kind === "orphan-nested-list") {
          return (
            <li key={rowKey} className={textColorClass}>
              <RichTextList
                block={entry.nestedList}
                depth={depth + 1}
                textColorClass={textColorClass}
                linkColorClass={linkColorClass}
                renderBlock={renderBlock}
                bodyTextClass={bodyTextClass}
              />
            </li>
          );
        }

        return (
          <RichTextListItem
            key={rowKey}
            node={entry.listItem}
            depth={depth}
            textColorClass={textColorClass}
            linkColorClass={linkColorClass}
            renderBlock={renderBlock}
            bodyTextClass={bodyTextClass}
          />
        );
      })}
    </ListTag>
  );
}

/**
 * One <li>. Title / paragraphs first; nested <ul>/<ol> inside same <li>
 * (from list-item.children or from sibling list via siblingNestedList).
 */
function RichTextListItem({
  node,
  depth,
  textColorClass,
  linkColorClass,
  renderBlock,
  siblingNestedList,
  bodyTextClass,
}) {
  if (!node) return null;

  if (node.type === "list") {
    return (
      <li className={textColorClass}>
        <RichTextList
          block={node}
          depth={depth + 1}
          textColorClass={textColorClass}
          linkColorClass={linkColorClass}
          renderBlock={renderBlock}
          bodyTextClass={bodyTextClass}
        />
      </li>
    );
  }

  const nestedFromSibling = siblingNestedList ? (
    <RichTextList
      block={siblingNestedList}
      depth={depth + 1}
      textColorClass={textColorClass}
      linkColorClass={linkColorClass}
      renderBlock={renderBlock}
      bodyTextClass={bodyTextClass}
    />
  ) : null;

  if (!node.children || node.children.length === 0) {
    return (
      <li className={textColorClass}>
        {renderTextNode(node, 0, textColorClass, linkColorClass)}
        {nestedFromSibling}
      </li>
    );
  }

  return (
    <li className={textColorClass}>
      <RichTextListItemBody
        nodes={node.children}
        depth={depth}
        textColorClass={textColorClass}
        linkColorClass={linkColorClass}
        renderBlock={renderBlock}
        bodyTextClass={bodyTextClass}
      />
      {nestedFromSibling}
    </li>
  );
}

/**
 * Inside <li>: paragraphs, text, then nested list blocks ??nested RichTextList stays inside this <li>.
 */
function RichTextListItemBody({
  nodes,
  depth,
  textColorClass,
  linkColorClass,
  renderBlock,
  bodyTextClass,
}) {
  if (!nodes || nodes.length === 0) return null;

  return nodes.map((child, idx) => {
    const key = `lib-${depth}-${idx}`;

    if (child.type === "list") {
      return (
        <RichTextList
          key={key}
          block={child}
          depth={depth + 1}
          textColorClass={textColorClass}
          linkColorClass={linkColorClass}
          renderBlock={renderBlock}
          bodyTextClass={bodyTextClass}
        />
      );
    }

    if (child.type === "paragraph") {
      return (
        <p
          key={key}
          className={`my-1 ${textColorClass} ${bodyTextClass} first:mt-0 last:mb-0`}
        >
          {child.children?.map((c, i) =>
            renderTextNode(c, i, textColorClass, linkColorClass)
          )}
        </p>
      );
    }

    if (child.type === "heading") {
      return renderBlock(child, key);
    }

    if (child.type === "text" || child.type === "link") {
      return (
        <React.Fragment key={key}>
          {renderTextNode(child, idx, textColorClass, linkColorClass)}
        </React.Fragment>
      );
    }

    if (child.type === "list-item") {
      return (
        <RichTextList
          key={key}
          block={{
            type: "list",
            format: child.format || DEFAULT_LIST_FORMAT,
            children: [child],
          }}
          depth={depth + 1}
          textColorClass={textColorClass}
          linkColorClass={linkColorClass}
          renderBlock={renderBlock}
          bodyTextClass={bodyTextClass}
        />
      );
    }

    return renderBlock(child, key);
  });
}

const DEFAULT_BODY_TEXT = "text-lg lg:text-xl leading-relaxed";
const DEFAULT_CAPTION_TEXT = "text-base lg:text-lg";

const HEADING_CLASSES = {
  default: {
    1: "mt-8 mb-4 text-5xl lg:text-6xl font-bold",
    2: "mt-7 mb-3 text-4xl lg:text-5xl font-bold",
    3: "mt-6 mb-3 text-3xl lg:text-4xl font-semibold",
    4: "mt-5 mb-2 text-2xl lg:text-3xl font-semibold",
    5: "mt-4 mb-2 text-xl lg:text-2xl font-semibold",
    6: "mt-3 mb-2 text-lg lg:text-xl font-semibold",
  },
  compact: {
    1: "mt-7 mb-3 text-3xl lg:text-4xl font-bold",
    2: "mt-6 mb-3 text-2xl lg:text-3xl font-bold",
    3: "mt-5 mb-2 text-xl lg:text-2xl font-semibold",
    4: "mt-4 mb-2 text-lg lg:text-xl font-semibold",
    5: "mt-3 mb-2 text-base lg:text-lg font-semibold",
    6: "mt-3 mb-2 text-sm lg:text-base font-semibold",
  },
};

const RichTextRenderer = ({
  content,
  className = "prose prose-lg lg:prose-xl max-w-none",
  textColorClass = "text-black",
  linkColorClass,
  bodyTextClass = DEFAULT_BODY_TEXT,
  captionTextClass = DEFAULT_CAPTION_TEXT,
  compactHeadings = false,
}) => {
  if (!content || !Array.isArray(content)) {
    return null;
  }

  const headingClasses =
    HEADING_CLASSES[compactHeadings ? "compact" : "default"];

  function renderBlock(block, index) {
    const children = block.children
      ? block.children.map((child, childIndex) =>
          renderTextNode(child, childIndex, textColorClass, linkColorClass)
        )
      : null;

    switch (block.type) {
      case "heading": {
        const level = block.level >= 1 && block.level <= 6 ? block.level : 2;
        const Tag = `h${level}`;
        return (
          <Tag
            key={index}
            className={`${headingClasses[level]} ${textColorClass}`}
          >
            {children}
          </Tag>
        );
      }
      case "paragraph":
        return (
          <p
            key={index}
            className={`my-4 ${textColorClass} ${bodyTextClass}`}
          >
            {children}
          </p>
        );

      case "list":
        return (
          <RichTextList
            key={index}
            block={block}
            depth={0}
            textColorClass={textColorClass}
            linkColorClass={linkColorClass}
            renderBlock={renderBlock}
            bodyTextClass={bodyTextClass}
          />
        );

      case "image":
        if (block.image && block.image.url) {
          return (
            <figure key={index} className="my-6">
              <img
                src={`${block.image.url}`}
                alt={block.image.alternativeText || block.image.name || ""}
                className="max-w-full h-auto rounded-md shadow-md mx-auto"
              />
              {block.image.caption && (
                <figcaption
                  className={`text-center ${captionTextClass} ${textColorClass} mt-2`}
                >
                  {block.image.caption}
                </figcaption>
              )}
            </figure>
          );
        }
        return null;

      case "quote":
        return (
          <blockquote
            key={index}
            className={`border-l-4 border-white/50 pl-4 italic my-6 ${textColorClass} ${bodyTextClass}`}
          >
            {children}
          </blockquote>
        );

      case "link":
        if (block.url) {
          const isInternal = /^\/(?!\/)/.test(block.url);
          const linkClass =
            linkColorClass || `${textColorClass} underline ${bodyTextClass}`;
          if (isInternal) {
            return (
              <RouterLink key={index} to={block.url} className={linkClass}>
                {children}
              </RouterLink>
            );
          }
          return (
            <a
              key={index}
              href={block.url}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {children}
            </a>
          );
        }
        return (
          <span key={index} className={textColorClass}>
            {children}
          </span>
        );

      case "table": {
        const rows = (block.children || []).filter((r) => r.type === "tableRow");
        if (rows.length === 0) return null;
        const flattenRowCells = (row) => {
          const raw = row.children || [];
          const cells = [];
          for (const c of raw) {
            if (c.type === "tableCell" || c.type === "tableHeaderCell") {
              cells.push(c);
            } else if (c.type === "tableHeader" && Array.isArray(c.children)) {
              for (const inner of c.children) {
                if (
                  inner.type === "tableCell" ||
                  inner.type === "tableHeaderCell"
                ) {
                  cells.push(inner);
                }
              }
            }
          }
          return cells;
        };
        return (
          <div
            key={index}
            className="my-6 overflow-x-auto rounded-xl border border-teal-200/50 shadow-sm"
          >
            <table className="w-full border-collapse min-w-[280px]">
              <tbody>
                {rows.map((row, rowIndex) => {
                  const cells = flattenRowCells(row);
                  return (
                    <tr
                      key={row.id ?? `row-${index}-${rowIndex}`}
                      className={`border-b border-teal-200/45 last:border-b-0 ${
                        rowIndex % 2 === 0 ? "bg-teal-50/55" : "bg-white/85"
                      }`}
                    >
                      {cells.map((cell, cellIndex) => {
                        const isTh =
                          cell.type === "tableHeaderCell" ||
                          cell.type === "tableHeader" ||
                          cell.header === true;
                        const CellTag = isTh ? "th" : "td";
                        const cellKey =
                          cell.id ?? `cell-${index}-${rowIndex}-${cellIndex}`;
                        const inner = (cell.children || []).map((child, ii) =>
                          renderBlock(child, `${cellKey}-${ii}`)
                        );
                        return (
                          <CellTag
                            key={cellKey}
                            scope={isTh ? "col" : undefined}
                            className={
                              isTh
                                ? "px-3 sm:px-4 py-2 sm:py-3 text-left text-sm sm:text-base font-semibold text-teal-950 bg-teal-900/12 border-r border-teal-200/40 last:border-r-0 align-top"
                                : "px-3 sm:px-4 py-2 sm:py-3 align-top text-sm sm:text-base text-teal-900/85 border-r border-teal-100/50 last:border-r-0"
                            }
                          >
                            {inner}
                          </CellTag>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }

      default:
        if (
          block.type === "tableRow" ||
          block.type === "tableCell" ||
          block.type === "tableHeaderCell" ||
          block.type === "tableHeader"
        ) {
          return null;
        }
        console.warn(
          "Unsupported block type in RichTextRenderer:",
          block.type,
          block
        );
        return null;
    }
  }

  return <div className={className}>{content.map(renderBlock)}</div>;
};

export default RichTextRenderer;
